import React, { useEffect, useState, FC } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useLocation, matchPath } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  ListSubheader,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import NavItem from './NavItem';
import { PAPEL1, PAPEL2, PAPEL3, PAPEL4, PAPEL5, PAPEL6, PAPEL7, PAPEL8, PAPEL9, PAPELDEFAULT } from '../../utils/rbac/papeis';
import HasAccessItemsMenu from '../../utils/rbac/componentsByRole';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import { listMenuUrl } from '../../utils/functions';
import { isNil } from 'lodash';
import { IMG_MENU, sectionsMenu } from 'src/constants';
import { getPaineisEmpresaAtualInit, setEmpresa } from 'src/redux/actions';
import AdminEmpSelection from 'src/components/AdminEmpSelection';
import { useSnackbar } from 'notistack';
import { versaoFrontend } from 'src/config';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';
import { Link as RouterLink } from 'react-router-dom';
import Logo from 'src/components/Logo';
import { MyIcon } from '../../utils/icons/sibarIcon';

function renderNavItems({ items, pathname, depth = 0, myPrivilege }) {
  const filteredItems = HasAccessItemsMenu({ items, myPrivilege });
  return (
    <List disablePadding>
      {filteredItems.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth, myPrivilege }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth, myPrivilege }) {
  const { title, href, icon, info, items, entidadeNome } = item;
  const key = `${title}-${depth}-${href || ''}`;

  const isRelatorio = title === 'Relatórios';

  const open = isRelatorio || matchPath(pathname, {
    path: href,
    exact: false
  });

  const navItemProps = {
    depth,
    icon,
    info,
    key,
    open: Boolean(open),
    title,
    svgIcon: Boolean(false),
    entidadeNome
  };

  const renderedNavItem = items
    ? (
      <NavItem {...navItemProps}>
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items,
          myPrivilege
        })}
      </NavItem>
    )
    : (
      <NavItem
        depth={depth}
        href={href}
        icon={item.icon}
        info={info}
        key={key}
        title={title}
        svgIcon={item.svgIcon}
        entidadeNome={entidadeNome}
      />
    );

  acc.push(renderedNavItem);
  return acc;
}

const useStyles = makeStyles((theme) => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    //top: 55,
    height: 'calc(100%)',
    boxShadow: '1px 0 2px rgba(0, 0, 0, 0.0)',
    '&::-paperAnchorDockedLeft': {
      borderRight: '5px solid #ffffff'
    },
    '&::-webkit-scrollbar': {
      height: '3px',
      width: '3px'
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '10px',
      backgroundColor: '#DFE9EB',
      border: '8px solid #FFFFFF',
    },
    '&::-webkit-scrollbar-track:hover': {
      backgroundColor: '#B8C0C2',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '6px',
      backgroundColor: 'rgba(154,154,154, 0.5)',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: 'rgba(154,154,154, 0.7)',
    },
    '&::-webkit-scrollbar-thumb:active': {
      backgroundColor: 'rgba(154,154,154, 0.9)',
    },
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  },
  iconOpenClose: {
    marginTop: 12,
    float: 'right',
    color: theme.palette.text.secondary
  },
  button: {
    color: theme.palette.text.secondary,
    textTransform: 'none',
    letterSpacing: 0,
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
    width: '100%'
  },
  rodape: {
    paddingTop: theme.spacing(2)
  },
  subHeader: {
    float: 'left'
  },
  subHeaderFont: {
    color: theme.palette.text.secondary
  },
  active: {
    color: `${theme.palette.primary.main}!important`
  },
  containerNav: {
    display: 'flex',
    flexDirection: 'column'
  },
  topoNav: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '15px 10px'
  },
  footerNav: {
    padding: '10px 0',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  lista: {
    padding: '0 8px'
  },
  subheader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    lineHeight: '35px'
  },
  line: {
    flex: 1,
    height: '1px',
    backgroundColor: theme.palette.divider,
    marginLeft: '16px',
    position: 'relative',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: 10,
    justifyContent: 'flex-end',
    minHeight: 50,
    flex: 1
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  logoItem: {
    marginLeft: 20
  },
  menuLateral: {
    boxShadow: '5px 0 6px rgba(0, 0, 0, 0.1)',
    flex: 'auto'
  },
}));

const NavBar = ({ handleDrawerClose, openInitial }) => {
  const classes = useStyles();
  const location = useLocation();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const theme = useTheme();

  const {
    myEmp,
    myPrivilege,
    paineisUsuarioAtual,
    categorias,
    paineisEmpresaAtual,
    isError,
    msg
  } = useSelector(
    (state) => ({
      myEmp: state.account.myEmp,
      myPrivilege: String(state.account.user?.privilegeHash || PAPELDEFAULT),
      paineisUsuarioAtual: state.account.user.paineis,
      categorias: state.categorias.categorias,
      paineisEmpresaAtual: state.account.paineisEmpresaAtual,
      isError: state.account.error,
      msg: state.account.msg
    }),
    shallowEqual
  );


  const userPermission = myPrivilege; // Defina as permissões do usuário
  const [sections, setSection] = useState([]);
  const [meusPaineis, setMeusPaineis] = useState([]);
  // Efeito para carregar os dados dos painéis da empresa ativa na plataforma
  useEffect(() => {
    // carrega apenas os paineis do tipo empresa
    // se o privilegio do usuário for maior ou igual que 21430, pega os paineis da empresa
    if (Number(myPrivilege) >= Number(PAPEL2)) {
      dispatch(getPaineisEmpresaAtualInit(myEmp.id));
    }
  }, [myEmp.id]);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(msg, {
        variant: 'info'
      });
    }
  }, [isError]);

  useEffect(() => {
    // se o privilegio do usuário for menor que 21430, pega os atribuídos a ele
    // se nao pega os atribuidos a empresa
    const paineisUser = Number(myPrivilege) < Number(PAPEL2) ? paineisUsuarioAtual : paineisEmpresaAtual;
    if (isNil(paineisUser) || paineisUser.length === 0) {
      if (Number(myPrivilege) < Number(PAPEL2)) {
        enqueueSnackbar('Usuário não possui painéis atribuídos', {
          variant: 'info'
        });
      }
      setMeusPaineis([]);
    } else {
      setMeusPaineis(paineisUser);
    }
  }, [paineisUsuarioAtual, paineisEmpresaAtual]);

  const handleEmp = (emp) => {
    dispatch(setEmpresa({ empresa: emp }));
    return true;
  };

  const hasSectionPermission = (sectionPermissions, userPermissions) => {
    if (!sectionPermissions) {
      // Se a seção não especifica permissões, ela é acessível para todos
      return true;
    }
    // Verifique se o usuário possui pelo menos uma das permissões da seção
    return sectionPermissions.some((permission) => userPermissions.includes(permission));
  };

  const filteredSections = sectionsMenu.filter((section) => {
    return hasSectionPermission(section.allowedRoles, userPermission);
  });

  // Função para verificar se o item "Relatorios" deve ser ocultado
  const ocultarItemRelatorio = (paineis) => {
    const paineisDoUsuario = paineis.length === 0;
    return paineisDoUsuario;
  }

  // Oculta o item "Relatorios" se necessário
  const displayedSections = ocultarItemRelatorio(meusPaineis)
    ? filteredSections.filter((section) => section.subheader !== 'Relatórios')
    : filteredSections;

  useEffect(() => {
    const sectionsloader = () => {
      if (isMountedRef.current && !isNil(categorias) && !isNil(meusPaineis) && meusPaineis.length > 0) {
        const fullPaineis = [];
        const categoriasOrdenadas = [...categorias].sort((a, b) => {
          // se ambos têm ordem
          if (a.ordem != null && b.ordem != null) {
            return a.ordem - b.ordem;
          }

          // se só A tem ordem, A vem primeiro
          if (a.ordem != null) return -1;

          // se só B tem ordem, B vem primeiro
          if (b.ordem != null) return 1;

          return 0;
        });

        // Percorre cada categoria
        categoriasOrdenadas.forEach(categ => {
          // Filtra os painéis que correspondem à categoria atual
          const categoriaPaineis = meusPaineis
            .filter(painel => categ.id === painel.categoriaId)
            // Mapeia os painéis filtrados para um novo formato esperado
            .map(painel => ({
              painelId: painel.painelId,
              name: painel.nome,
              entidadeNome: painel.entidadeNome,
              entidadeTipo: painel.entidadeTipo,
              icon: painel.icone,
              lojaId: painel.entidadeTipo === 'loja' ? painel.entidadeId : undefined
            }));
          // Verifica se há painéis para a categoria atual
          if (categoriaPaineis.length > 0) {
            fullPaineis.push({
              categoria: categ.name, // como os itens serão agrupados
              categoriaId: categ.id,
              paineis: categoriaPaineis
            });
          }
        });
        // Chama a função para criar a estrutura do menu
        const lista = listMenuUrl({
          baseUrl: '',
          itens: fullPaineis,
          empId: myEmp.id,
          subHeaderName: 'categoria',
          subHeaderIdName: 'categoriaId',
          itemName: 'paineis',
          subItemIdName: 'painelId',
          subItemLojaIdName: 'lojaId',
          papeis: [PAPEL1, PAPEL2, PAPEL3, PAPEL4, PAPEL5, PAPEL6, PAPEL7, PAPEL8, PAPEL9]
        });

        // Atualiza o sections com a lista gerada
        setSection(lista);
      }
    };

    sectionsloader();
  }, [categorias, isMountedRef, meusPaineis]);

  // Atualiza o items do primeiro elemento do sectionsMenu com os dashboards
  sectionsMenu[0].items = sections;

  const content = (
    <Box display="flex" flexDirection="column" className={classes.menuLateral}>
      <>
        <div className={classes.menuItem}>
          <div className={classes.logoItem}>
            <RouterLink to="/">
              <Logo className={classes.logo} />
            </RouterLink>
          </div>
          <div className={classes.drawerHeader}>
            <IconButton onClick={handleDrawerClose}>
              <MyIcon />
            </IconButton>
          </div>
        </div>
        <AdminEmpSelection handleEmp={handleEmp} />
        <Box>
          {displayedSections.map((section, index) => (
            <React.Fragment key={section.subheader || index}>
              <List
                disablePadding
                className={classes.lista}
                subheader={
                  <ListSubheader disableGutters disableSticky className={classes.subheader}>
                    {section.subheader}
                    {section.subheader && <div className={classes.line}></div>}
                  </ListSubheader>
                }
              >
                {renderNavItems({
                  items: section.items,
                  pathname: location.pathname,
                  myPrivilege: myPrivilege
                })}
              </List>
            </React.Fragment>
          ))
          }
        </Box>
        <Divider />
        <Box className={classes.footerNav} display="flex">
          <Typography
            align="center"
            display="inline"
            variant="body2"
            color="textSecondary"
          >
            Versão {versaoFrontend}-{userPermission}
          </Typography>
        </Box>
      </>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={handleDrawerClose}
          open={openInitial}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open={openInitial}
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );

};

NavBar.propTypes = {
  handleDrawerClose: PropTypes.func,
  openInitial: PropTypes.bool,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;